import React, { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import api from '../services/api';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Site brand palette — matches the header, buttons and tenant theming
// Injected once: cluster pulse + marker hover. Kept here so the map component is
// self-contained and respects prefers-reduced-motion.
const MAP_STYLE_ID = 'trados-map-anim';
const MAP_CSS = `
.trados-cluster { background: transparent !important; border: none !important; }
.trados-cluster-dot {
  position: relative; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; font-weight: 700;
  box-shadow: 0 4px 14px rgba(43,75,106,.35);
  transition: transform .18s ease, box-shadow .18s ease;
  animation: trados-bob 4.5s ease-in-out infinite;
}
.trados-cluster:hover .trados-cluster-dot {
  transform: scale(1.12);
  box-shadow: 0 6px 20px rgba(43,75,106,.45);
}
.trados-cluster-pulse {
  position: absolute; top: 0; left: 0; z-index: 1;
  border-radius: 50%; background: rgba(245,138,90,.45);
  animation: trados-pulse 3.2s cubic-bezier(.24,.6,.36,1) infinite;
}
@keyframes trados-pulse {
  0%   { transform: scale(1);   opacity: .55; }
  70%  { transform: scale(2.1); opacity: 0; }
  100% { transform: scale(2.1); opacity: 0; }
}
@keyframes trados-bob {
  0%, 100% { translate: 0 0; }
  50%      { translate: 0 -3px; }
}
.trados-marker { transition: fill-opacity .15s ease; }
.trados-marker:hover { fill-opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .trados-cluster-dot { animation: none; }
  .trados-cluster-pulse { animation: none; opacity: 0; }
}
`;

function ensureMapStyles() {
  if (typeof document === 'undefined' || document.getElementById(MAP_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = MAP_STYLE_ID;
  el.textContent = MAP_CSS;
  document.head.appendChild(el);
}

const ORANGE = '#F58A5A';       // Trados accent
const BLUE = '#2B4B6A';         // Trados primary
const BLUE_DEEP = '#1E3A52';    // hover / depth

/**
 * Real interactive world map for the public directory.
 *
 * Plots EVERY company (GET /public/directory/map-points) as a marker on a
 * Leaflet/CARTO basemap, clustered by area (leaflet.markercluster). Each marker
 * popup shows the company name + location + a link to its profile. Zoom/pan,
 * cluster expansion (spiderfy) reveals individual companies — no simulation,
 * 100% of the listings are on the map.
 *
 * Leaflet + markercluster are imported dynamically so they only load on this
 * page and stay compatible across React versions.
 */
const DirectoryWorldMap = () => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [total, setTotal] = useState(0);
  const [countries, setCountries] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const L = (await import('leaflet')).default;
        await import('leaflet.markercluster');

        const res = await api.get('/public/directory/map-points');
        if (cancelled) return;
        const points = (res.data && res.data.points) || [];
        if (!points.length) { setStatus('empty'); return; }

        setTotal(res.data.total || points.length);
        setCountries(new Set(points.map((p) => p.country)).size);

        if (!containerRef.current || mapRef.current) return;
        const map = L.map(containerRef.current, {
          worldCopyJump: true,
          minZoom: 2,
          maxZoom: 12,
          scrollWheelZoom: false,
          attributionControl: true,
        }).setView([25, 12], 2);
        mapRef.current = map;

        ensureMapStyles();

        // Basemap.
        //
        // CARTO stopped serving anonymous basemap tiles: requests without an
        // API key still return 200, but every tile comes back stamped
        // "API KEY REQUIRED", which is what showed across this map.
        //
        // Default is Esri's Light Gray canvas (base + a separate label layer),
        // which needs no key and matches the light canvas this design was built
        // on. Set REACT_APP_CARTO_API_KEY to go back to CARTO — the look is
        // identical to before and the watermark is gone once a key is present.
        const cartoKey = process.env.REACT_APP_CARTO_API_KEY;
        if (cartoKey) {
          L.tileLayer(
            `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?api_key=${cartoKey}`,
            {
              attribution: '&copy; OpenStreetMap &copy; CARTO',
              subdomains: 'abcd',
              maxZoom: 19,
            },
          ).addTo(map);
        } else {
          L.tileLayer(
            'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
            { attribution: '&copy; Esri', maxZoom: 16 },
          ).addTo(map);
          // Labels go in the default tile pane, added after the base so they
          // draw on top of it — but still under every marker pane, so a place
          // name can never sit over a cluster bubble.
          L.tileLayer(
            'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
            { attribution: '', maxZoom: 16 },
          ).addTo(map);
        }

        const cluster = L.markerClusterGroup({
          chunkedLoading: true,
          spiderfyDistanceMultiplier: 1.6,
          animate: true,
          animateAddingMarkers: true,
          showCoverageOnHover: false,
          maxClusterRadius: 45,
          iconCreateFunction: (c) => {
            const n = c.getChildCount();
            const size = n < 10 ? 34 : n < 100 ? 42 : 52;
            // Bigger clusters pulse slower, so the map breathes rather than flickers
            const delay = (n % 5) * 0.4;
            return L.divIcon({
              html:
                `<div class="trados-cluster-pulse" style="width:${size}px;height:${size}px;animation-delay:${delay}s"></div>` +
                `<div class="trados-cluster-dot" style="background:${BLUE};color:#fff;width:${size}px;height:${size}px;border:3px solid ${ORANGE};font-size:${n < 100 ? 13 : 12}px;animation-delay:${delay}s">${n}</div>`,
              className: 'trados-cluster',
              iconSize: L.point(size, size),
            });
          },
        });

        const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => (
          { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
        ));

        points.forEach((p) => {
          const m = L.circleMarker([p.lat, p.lng], {
            radius: 5,
            fillColor: ORANGE,
            color: BLUE,
            weight: 1.5,
            fillOpacity: 0.95,
            className: 'trados-marker',
          });
          const loc = [p.city, p.country].filter(Boolean).join(', ');
          const logo = p.logo_url
            ? `<img src="${esc(p.logo_url)}" alt="" style="width:40px;height:40px;object-fit:contain;border-radius:8px;background:#fff;border:1px solid #e2e8f0;margin-bottom:6px"/>` : '';
          m.bindPopup(
            `<div style="min-width:170px;font-family:inherit">${logo}` +
            `<div style="font-weight:700;color:${BLUE};font-size:15px;line-height:1.2">${esc(p.name)}</div>` +
            `<div style="color:#64748b;font-size:13px;margin:2px 0 8px">${esc(loc)}</div>` +
            `<a href="/directory/${esc(p.slug)}" style="color:${BLUE};font-weight:600;font-size:13px;text-decoration:none">View profile &rarr;</a></div>`,
            { closeButton: true }
          );
          cluster.addLayer(m);
        });

        map.addLayer(cluster);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <p className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>
            Companies on Trados
          </p>
          <h3 className="text-2xl md:text-3xl font-extrabold" style={{ color: BLUE }}>
            <CountUp end={total} duration={2} className="tabular-nums" /> trading companies worldwide
          </h3>
        </div>
        {countries > 0 && (
          <div className="text-sm text-gray-500">
            Across <span className="font-bold" style={{ color: BLUE }}>{countries}</span> countries — click a cluster to explore
          </div>
        )}
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm" style={{ background: '#eef2f6' }}>
        <div ref={containerRef} style={{ height: 'clamp(360px, 55vh, 560px)', width: '100%' }} />
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none">
            Loading map…
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            Map unavailable right now.
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectoryWorldMap;
